import React, { useState, useEffect, useRef } from 'react'
import SideBar from '../components/SideBar'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import '../styles/EditCustomerProfile.css';
import 'react-phone-input-2/lib/style.css';
import PhoneInput from "react-phone-input-2";
import Select from "react-select";
import { customerService } from '../services/customerServices';

function EditCustomerProfile() {
    const [step, setStep] = useState(1);
    const location = useLocation();
    const customer = location.state?.customer || {};
    const navigate = useNavigate();

    // State initialization with actual API data structure
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [addressLine1, setAddressLine1] = useState("");
    const [addressLine2, setAddressLine2] = useState("");
    const [addressLine3, setAddressLine3] = useState("");
    const [city, setCity] = useState("");
    const [postalCode, setPostalCode] = useState(""); // ADDED
    const [region, setRegion] = useState("");
    const [country, setCountry] = useState("");
    const [status, setStatus] = useState(null);
    const [note, setNote] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Validation states
    const [firstNameError, setFirstNameError] = useState("");
    const [lastNameError, setLastNameError] = useState("");
    const [emailError, setEmailError] = useState("");
    const [phoneError, setPhoneError] = useState("");
    const [line1Error, setLine1Error] = useState("");
    const [cityError, setCityError] = useState("");
    const [postalCodeError, setPostalCodeError] = useState(""); // ADDED

    // Email validation states
    const [emailChecking, setEmailChecking] = useState(false);
    const [emailAvailable, setEmailAvailable] = useState(null); 
    const [emailCheckMessage, setEmailCheckMessage] = useState("");

    // Phone validation states
    const [phoneChecking, setPhoneChecking] = useState(false);
    const [phoneAvailable, setPhoneAvailable] = useState(null);
    const [phoneCheckMessage, setPhoneCheckMessage] = useState("");

    // Store original values for comparison
    const [originalEmail, setOriginalEmail] = useState("");
    const [originalPhone, setOriginalPhone] = useState("");
    const [originalPostalCode, setOriginalPostalCode] = useState(""); // ADDED

    // Initialize form with customer data
    useEffect(() => {
        if (customer) {
            const customerEmail = customer.email || "";
            const customerPhone = customer.phone_number || customer.mobile || "";
            
            setFirstName(customer.first_name || customer.firstName || "");
            setLastName(customer.last_name || customer.surname || "");
            setEmail(customerEmail);
            setPhoneNumber(customerPhone);
            setAddressLine1(customer.address_line1 || customer.address?.line1 || "");
            setAddressLine2(customer.address_line2 || customer.address?.line2 || "");
            setAddressLine3(customer.address_line3 || customer.address?.line3 || "");
            setCity(customer.city || customer.address?.city || "");
            setPostalCode(customer.postcode || customer.address?.postcode || customer.postalCode || ""); // ADDED
            setRegion(customer.region || customer.address?.county || "");
            setCountry(customer.country || customer.address?.country || "");
            setNote(customer.status_note || customer.notes || customer.note || "");
            
            // Store original values
            setOriginalEmail(customerEmail);
            setOriginalPhone(customerPhone);
            setOriginalPostalCode(customer.postcode || customer.address?.postcode || customer.postalCode || ""); // ADDED
            
            // Set status with proper formatting
            const currentStatus = customer.current_status || customer.status;
            if (currentStatus) {
                setStatus({
                    value: currentStatus,
                    label: currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1)
                });
            }
        }
    }, [customer]);

    // Function to get phone digit count
    const getPhoneDigitCount = () => {
        const digitsOnly = phoneNumber.replace(/\D/g, "");
        const match = digitsOnly.match(/^94|^91|^44|^1|^33|^49|^61|^65|^81/);
        let nationalNumber = digitsOnly;
        if (match) {
            nationalNumber = digitsOnly.slice(match[0].length);
        }
        return nationalNumber.length;
    };

    const hasValidPhoneDigits = getPhoneDigitCount() >= 9;

    // Check email and phone availability simultaneously
    const checkEmailAndPhoneAvailability = async () => {
        let emailValid = true;
        let phoneValid = true;

        // Check email format (only if changed)
        if (email !== originalEmail) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email)) {
                emailValid = false;
            }
        }

        // Check phone format (only if changed)
        if (phoneNumber !== originalPhone) {
            const digitsOnly = phoneNumber.replace(/\D/g, "");
            const match = digitsOnly.match(/^94|^91|^44|^1|^33|^49|^61|^65|^81/);
            let nationalNumber = digitsOnly;
            if (match) {
                nationalNumber = digitsOnly.slice(match[0].length);
            }
            if (nationalNumber.length !== 9) {
                phoneValid = false;
            }
        }

        // If neither changed, no need to check
        if (email === originalEmail && phoneNumber === originalPhone) {
            return { emailAvailable: true, phoneAvailable: true };
        }

        // Set both to checking state if needed
        if (email !== originalEmail) {
            setEmailChecking(true);
            setEmailAvailable(null);
            setEmailCheckMessage("Checking email availability...");
        }

        if (phoneNumber !== originalPhone) {
            setPhoneChecking(true);
            setPhoneAvailable(null);
            setPhoneCheckMessage("Checking phone availability...");
        }

        try {
            // Prepare API calls for changed fields only
            const apiCalls = [];
            
            if (email !== originalEmail && emailValid) {
                apiCalls.push(customerService.checkEmailAvailability(email));
            }
            
            if (phoneNumber !== originalPhone && phoneValid) {
                apiCalls.push(customerService.checkPhoneAvailability(phoneNumber));
            }

            // Make API calls simultaneously
            const results = await Promise.all(apiCalls);

            // Process results
            let emailResult = { available: true, message: "" };
            let phoneResult = { available: true, message: "" };

            if (email !== originalEmail && emailValid) {
                emailResult = results.shift();
                setEmailAvailable(emailResult.available);
                setEmailCheckMessage(emailResult.message);
            }

            if (phoneNumber !== originalPhone && phoneValid) {
                phoneResult = results.shift();
                setPhoneAvailable(phoneResult.available);
                setPhoneCheckMessage(phoneResult.message);
            }

            return {
                emailAvailable: emailResult.available,
                phoneAvailable: phoneResult.available
            };

        } catch (error) {
            console.error("Availability check error:", error);
            
            if (email !== originalEmail) {
                setEmailAvailable(null);
                setEmailCheckMessage("Error checking email availability");
            }
            
            if (phoneNumber !== originalPhone) {
                setPhoneAvailable(null);
                setPhoneCheckMessage("Error checking phone availability");
            }
            
            return {
                emailAvailable: false,
                phoneAvailable: false
            };
        } finally {
            setEmailChecking(false);
            setPhoneChecking(false);
        }
    };

    const nextStep = async () => {
        // Run validation only on Step 1
        if (step === 1) {
            const isValid = validateBasicDetails();
            if (!isValid) return; // stop if validation fails

            // Check both email and phone availability simultaneously when clicking Next
            const availability = await checkEmailAndPhoneAvailability();
            
            // Check email availability result (only if changed)
            if (email !== originalEmail) {
                if (availability.emailAvailable === false) {
                    setEmailError("");
                    return;
                }

                if (availability.emailAvailable === null) {
                    setEmailError("Unable to verify email availability. Please try again.");
                    return;
                }
            }

            // Check phone availability result (only if changed)
            if (phoneNumber !== originalPhone) {
                if (availability.phoneAvailable === false) {
                    setPhoneError("");
                    return;
                }

                if (availability.phoneAvailable === null) {
                    setPhoneError("Unable to verify phone availability. Please try again.");
                    return;
                }
            }

            // Only proceed if both are available (or unchanged)
            if ((email === originalEmail || availability.emailAvailable === true) && 
                (phoneNumber === originalPhone || availability.phoneAvailable === true)) {
                setStep((prev) => Math.min(prev + 1, 3));
            }
        } else if (step === 2) {
            const isValid = validateAddressDetails();
            if (!isValid) return;
            setStep((prev) => Math.min(prev + 1, 3));
        }
    };

    const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

    // Function to handle direct step click - NO VALIDATION
    const handleStepClick = (stepNumber) => {
        // Don't allow clicking on current step
        if (stepNumber === step) return;
        
        // Direct navigation without validation
        setStep(stepNumber);
    };

    const steps = [
        { label: "Basic details", sub: "Enter your basic details" },
        { label: "Address", sub: "Enter your address details" },
        { label: "Status", sub: "Update the status" },
    ];

    const regionOptions = {
        India: [
            "Andhra Pradesh",
            "Delhi",
            "Karnataka",
            "Maharashtra",
            "Tamil Nadu",
            "West Bengal",
        ],
        "Sri Lanka": [
            "Central Province",
            "Eastern Province",
            "Northern Province",
            "Southern Province",
            "Western Province",
            "North Central Province",
            "North Western Province",
            "Sabaragamuwa Province",
            "Uva Province",
        ],
        Canada: ["Alberta", "British Columbia", "Manitoba", "Ontario", "Quebec"],
        "United Kingdom": [
            "England - Bedfordshire",
            "England - Berkshire",
            "England - Bristol",
            "England - Buckinghamshire",
            "England - Cambridgeshire",
            "England - Cheshire",
            "England - City of London",
            "England - Cornwall",
            "England - Cumbria",
            "England - Derbyshire",
            "England - Devon",
            "England - Dorset",
            "England - Durham",
            "England - East Riding of Yorkshire",
            "England - East Sussex",
            "England - Essex",
            "England - Gloucestershire",
            "England - Greater London",
            "England - Greater Manchester",
            "England - Hampshire",
            "England - Herefordshire",
            "England - Hertfordshire",
            "England - Isle of Wight",
            "England - Kent",
            "England - Lancashire",
            "England - Leicestershire",
            "England - Lincolnshire",
            "England - Merseyside",
            "England - Norfolk",
            "England - North Yorkshire",
            "England - Northamptonshire",
            "England - Northumberland",
            "England - Nottinghamshire",
            "England - Oxfordshire",
            "England - Rutland",
            "England - Shropshire",
            "England - Somerset",
            "England - South Yorkshire",
            "England - Staffordshire",
            "England - Suffolk",
            "England - Surrey",
            "England - Sussex",
            "England - Tyne & Wear",
            "England - Warwickshire",
            "England - West Midlands",
            "England - West Sussex",
            "England - West Yorkshire",
            "England - Wiltshire",
            "England - Worcestershire",

            "Northern Ireland - Antrim",
            "Northern Ireland - Armagh",
            "Northern Ireland - Down",
            "Northern Ireland - Fermanagh",
            "Northern Ireland - Londonderry",
            "Northern Ireland - Tyrone",

            "Scotland - City of Aberdeen",
            "Scotland - Aberdeenshire",
            "Scotland - Angus",
            "Scotland - Argyll",
            "Scotland - Ayrshire",
            "Scotland - Banffshire",
            "Scotland - Berwickshire",
            "Scotland - Bute",
            "Scotland - Caithness",
            "Scotland - Clackmannanshire",
            "Scotland - Dumfriesshire",
            "Scotland - Dunbartonshire",
            "Scotland - City of Dundee",
            "Scotland - East Lothian",
            "Scotland - City of Edinburgh",
            "Scotland - Fife",
            "Scotland - City of Glasgow",
            "Scotland - Inverness-shire",
            "Scotland - Kincardineshire",
            "Scotland - Kinross-shire",
            "Scotland - Kirkcudbrightshire",
            "Scotland - Lanarkshire",
            "Scotland - Midlothian",
            "Scotland - Moray",
            "Scotland - Nairnshire",
            "Scotland - Orkney",
            "Scotland - Peeblesshire",
            "Scotland - Perthshire",
            "Scotland - Renfrewshire",
            "Scotland - Ross and Cromarty",
            "Scotland - Roxburghshire",
            "Scotland - Selkirkshire",
            "Scotland - Shetland",
            "Scotland - Stirlingshire",
            "Scotland - Sutherland",
            "Scotland - West Lothian",
            "Scotland - Wigtownshire",

            "Wales - Blaenau Gwent",
            "Wales - Bridgend",
            "Wales - Caerphilly",
            "Wales - Carmarthenshire",
            "Wales - Ceredigion",
            "Wales - Swansea",
            "Wales - Cardiff",
            "Wales - Conwy",
            "Wales - Denbighshire",
            "Wales - Flintshire",
            "Wales - Gwynedd",
            "Wales - Isle of Anglesey",
            "Wales - Merthyr Tydfil",
            "Wales - Monmouthshire",
            "Wales - Neath Port Talbot",
            "Wales - Newport",
            "Wales - Pembrokeshire",
            "Wales - Powys",
            "Wales - Rhondda Cynon Taf",
            "Wales - Torfaen",
            "Wales - Vale of Glamorgan",
            "Wales - Wrexham",
        ],
        Iran: ["Tehran", "Isfahan", "Fars", "Mazandaran", "Khorasan Razavi"],
    };

    const statusOptions = [
        { value: "approved", label: "Approved" },
        { value: "awaiting", label: "Awaiting" },
        { value: "suspended", label: "Suspended" },
        { value: "rejected", label: "Rejected" },
    ];

    const validateBasicDetails = () => {
        let valid = true;

        // First name validation
        if (!firstName.trim()) {
            setFirstNameError("First name is required.");
            valid = false;
        } else if (!/^[A-Za-z\s]+$/.test(firstName)) {
            setFirstNameError("First name must contain only letters.");
            valid = false;
        } else if (firstName.trim().length < 3) {
            setFirstNameError("First name must be at least 3 letters long.");
            valid = false;
        } else {
            setFirstNameError("");
        }

        // Last name validation
        if (!lastName.trim()) {
            setLastNameError("Last name is required.");
            valid = false;
        } else if (!/^[A-Za-z\s]+$/.test(lastName)) {
            setLastNameError("Last name must contain only letters.");
            valid = false;
        } else if (lastName.trim().length < 3) {
            setLastNameError("Last name must be at least 3 letters long.");
            valid = false;
        } else {
            setLastNameError("");
        }

        // Email validation
        if (!email.trim()) {
            setEmailError("Email is required.");
            valid = false;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setEmailError("Please enter a valid email address.");
            valid = false;
        } else {
            // Clear basic format error, but keep availability error if any
            if (!emailError.includes("already registered")) {
                setEmailError("");
            }
        }

        // Phone validation
        if (!phoneNumber.trim()) {
            setPhoneError("Phone number is required");
            valid = false;
        } else {
            const digitsOnly = phoneNumber.replace(/\D/g, "");
            const match = digitsOnly.match(/^94|^91|^44|^1|^33|^49|^61|^65|^81/);
            let nationalNumber = digitsOnly;
            if (match) {
                nationalNumber = digitsOnly.slice(match[0].length);
            }
            
            if (nationalNumber.length > 10) {
                setPhoneError("Phone number must not exceed 10 digits (excluding country code).");
                valid = false;
            } else {
                // Clear basic format error, but keep availability error if any
                if (!phoneError.includes("already registered")) {
                    setPhoneError("");
                }
            }
        }

        return valid;
    };

    const validateAddressDetails = () => {
        let valid = true;

        if (!addressLine1.trim()) {
            setLine1Error("Address line 1 is required.");
            valid = false;
        } else {
            setLine1Error("");
        }

        if (!city.trim()) {
            setCityError("City is required.");
            valid = false;
        } else {
            setCityError("");
        }

        // Postal code validation
        if (!postalCode.trim()) {
            setPostalCodeError("Postal code is required.");
            valid = false;
        } else if (!/^[A-Za-z0-9\s\-]+$/.test(postalCode)) {
            setPostalCodeError("Please enter a valid postal code.");
            valid = false;
        } else {
            setPostalCodeError("");
        }

        return valid;
    };

    const handleUpdate = async () => {
        setLoading(true);
        setError("");
        
        try {
            // Prepare update data - only include fields that are different from original
            const updateData = {
                first_name: firstName,
                last_name: lastName,
                // Only include email if it's different from original
                ...(email !== originalEmail && { email: email }),
                // Only include phone if it's different from original
                ...(phoneNumber !== originalPhone && { phone_number: phoneNumber }),
                address_line1: addressLine1,
                address_line2: addressLine2,
                address_line3: addressLine3,
                city: city,
                postcode: postalCode, // ADDED
                country_name: country,
                region: region,
            };

            // Remove empty fields to avoid unnecessary updates
            Object.keys(updateData).forEach(key => {
                if (updateData[key] === undefined || updateData[key] === null) {
                    delete updateData[key];
                }
            });

            console.log("Updating customer with data:", updateData);

            // Update customer profile
            await customerService.updateCustomer(customer.Customer_id || customer.id, updateData);

            // If status was changed, update it separately
            const currentStatus = customer.current_status || customer.status;
            if (status && status.value !== currentStatus) {
                await customerService.updateCustomerStatus(customer.Customer_id || customer.id, {
                    user_status: status.value,
                    notes: note || `Status changed to ${status.value} by admin`
                });
            }

            // Create updated customer object with proper structure for CustomerDetails
            const updatedCustomer = {
                ...customer,
                // Basic details
                firstName: firstName,
                surname: lastName,
                first_name: firstName,
                last_name: lastName,
                email: email,
                mobile: phoneNumber,
                phone_number: phoneNumber,

                // Address - maintain both old and new structure for compatibility
                address_line1: addressLine1,
                address_line2: addressLine2,
                address_line3: addressLine3,
                city: city,
                postcode: postalCode, // ADDED
                region: region,
                country: country,
                address: {
                    line1: addressLine1,
                    line2: addressLine2,
                    line3: addressLine3,
                    city: city,
                    postcode: postalCode, // ADDED
                    county: region,
                    country: country,
                },

                // Status
                current_status: status?.value || currentStatus,
                status: status?.value || currentStatus,
                status_note: note,
                notes: note || customer.notes,
            };

            // Navigate back with updated customer data
            navigate("/customer-details", { 
                state: { 
                    customer: updatedCustomer,
                    message: "Customer profile updated successfully!"
                } 
            });

        } catch (err) {
            console.error("Error updating customer:", err);
            
            // Handle specific error cases
            if (err.response?.data?.errors?.email) {
                setError("Email address is already taken. Please use a different email.");
            } else if (err.response?.data?.errors?.phone_number) {
                setError("Phone number is already registered. Please use a different phone number.");
            } else if (err.response?.data?.message) {
                setError(err.response.data.message);
            } else {
                setError("Failed to update customer profile. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        // Reset form to original customer data
        setFirstName(customer.first_name || customer.firstName || "");
        setLastName(customer.last_name || customer.surname || "");
        setEmail(originalEmail);
        setPhoneNumber(originalPhone);
        setAddressLine1(customer.address_line1 || customer.address?.line1 || "");
        setAddressLine2(customer.address_line2 || customer.address?.line2 || "");
        setAddressLine3(customer.address_line3 || customer.address?.line3 || "");
        setCity(customer.city || customer.address?.city || "");
        setPostalCode(originalPostalCode); // ADDED
        setRegion(customer.region || customer.address?.county || "");
        setCountry(customer.country || customer.address?.country || "");
        setNote(customer.status_note || customer.notes || customer.note || "");
        
        const currentStatus = customer.current_status || customer.status;
        if (currentStatus) {
            setStatus({
                value: currentStatus,
                label: currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1)
            });
        } else {
            setStatus(null);
        }
        
        // Clear validation states
        setFirstNameError("");
        setLastNameError("");
        setEmailError("");
        setPhoneError("");
        setLine1Error("");
        setCityError("");
        setPostalCodeError(""); // ADDED
        setError("");
        
        // Clear availability checks
        setEmailAvailable(null);
        setEmailCheckMessage("");
        setPhoneAvailable(null);
        setPhoneCheckMessage("");
        
        setStep(1);
    };

    // Get current country option for select
    const currentCountryOption = country ? { value: country, label: country } : null;
    // Get current region option for select
    const currentRegionOption = region ? { value: region, label: region } : null;

    return (
        <div className="member-details-layout">
            <SideBar />
    
            <div className="member-details-main">
                {/* Header */}
                <div className="edit-header">
                    <h2>Edit Profile</h2>
                    <p>You can edit your details to update your profile</p>
                </div>
    
                {/* Breadcrumb */}
                <div className="breadcrumb">
                    <Link to="/dashboard" className="breadcrumb-link">Home</Link>
                    <span className="breadcrumb-separator">{">"}</span>
                    <Link to="/manage-customer" className="breadcrumb-link">Manage Customer</Link>
                    <span className="breadcrumb-separator">{">"}</span>
                    <span className="breadcrumb-current">Edit Profile</span>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="error-message" style={{margin: '1rem 0', padding: '0.75rem', background: '#f8d7da', color: '#721c24', borderRadius: '4px'}}>
                        {error}
                    </div>
                )}

                <div className="multi-form-container">
                    <div className="progress-bar">
                        {steps.map((item, i) => {
                            const stepNum = i + 1;
                            const isActive = step === stepNum;
                            const isCompleted = step > stepNum;
                            return (
                                <div key={i} className="progress-step">
                                    <button
                                        type="button"
                                        className={`circle ${
                                            isCompleted ? "completed" : isActive ? "active" : ""
                                        } clickable`}
                                        onClick={() => handleStepClick(stepNum)}
                                        disabled={isActive}
                                        title={`Go to Step ${stepNum}: ${item.label}`}
                                    >
                                        {isCompleted ? "✓" : stepNum}
                                    </button>
                                    <div className="step-text">
                                        <p className={`step-label ${isActive ? "active-label" : ""}`}>
                                            {item.label}
                                        </p>
                                        <p className={`step-subtext ${isActive ? "active-sub" : ""}`}>
                                            {item.sub}
                                        </p>
                                    </div>
                                    {i < steps.length - 1 && (
                                        <div
                                            className={`line ${
                                                isCompleted ? "line-filled" : "line-empty"
                                            }`}
                                        ></div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Step content */}
                    <div className="step-content">
                        {step === 1 && (
                            <div>
                                <h3>Basic Details</h3>
                                <hr/>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>
                                            First name <span className="required">*</span>
                                        </label>
                                        <input 
                                            type="text" 
                                            value={firstName}
                                            onChange={(e) => {
                                                setFirstName(e.target.value);
                                                if (firstNameError) setFirstNameError(""); // clear error when typing
                                            }}
                                            placeholder="Enter first name" 
                                        />
                                        <div className="error-container">
                                            {firstNameError && <p className="error-text">{firstNameError}</p>}
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>
                                            Last name <span className="required">*</span>
                                        </label>
                                        <input 
                                            type="text" 
                                            value={lastName}
                                            onChange={(e) => {
                                                setLastName(e.target.value);
                                                if (lastNameError) setLastNameError("");
                                            }}
                                            placeholder="Enter last name" 
                                        />
                                        <div className="error-container">
                                            {lastNameError && <p className="error-text">{lastNameError}</p>}
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>
                                            Email <span className="required">*</span>
                                        </label>
                                        <input 
                                            type="email" 
                                            value={email}
                                            disabled
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setEmail(value);
                                                
                                                // Reset email availability status when user types
                                                setEmailAvailable(null);
                                                setEmailCheckMessage("");
                                                
                                                if (emailError && !emailError.includes("already registered")) {
                                                    setEmailError("");
                                                }
                                            }}
                                            placeholder="Enter email" 
                                            className={email !== originalEmail ? (emailAvailable === false ? "email-taken" : emailAvailable === true ? "email-available" : "") : ""}
                                        />
                                        
                                        {/* Email availability status - only show if email has changed and after checking */}
                                        {email !== originalEmail && (
                                            <>
                                                {emailChecking && (
                                                    <div className="email-status checking">
                                                        Checking email availability...
                                                    </div>
                                                )}
                                                
                                                {!emailChecking && emailCheckMessage && (
                                                    <div className={`email-status ${emailAvailable === true ? 'available' : emailAvailable === false ? 'taken' : ''}`}>
                                                        {emailCheckMessage}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        
                                        <div className="error-container">
                                            {emailError && <p className="error-text">{emailError}</p>}
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>
                                            Phone Number <span className="required">*</span>
                                        </label>
                                        <div className={`phone-input-container ${phoneNumber !== originalPhone ? (phoneAvailable === false ? 'phone-taken' : phoneAvailable === true ? 'phone-available' : '') : ''}`}>
                                            <PhoneInput
                                                country={"gb"}
                                                value={phoneNumber}
                                                onChange={(value, country) => {
                                                    const digitsOnly = value.replace(/\D/g, "");
                                                    const dialCode = country?.dialCode || "";
                                                    const dialCodeLength = dialCode.length;
                                                    let nationalNumber = digitsOnly.slice(dialCodeLength);

                                                    if (nationalNumber.length > 9) {
                                                        nationalNumber = nationalNumber.slice(0, 9);
                                                    }

                                                    const finalValue = `+${dialCode}${nationalNumber}`;
                                                    setPhoneNumber(finalValue);
                                                    
                                                    // Reset phone availability status when user types
                                                    setPhoneAvailable(null);
                                                    setPhoneCheckMessage("");
                                                    
                                                    if (phoneError && !phoneError.includes("already registered")) {
                                                        setPhoneError("");
                                                    }
                                                }}
                                                onKeyDown={(e) => {
                                                    const digitsOnly = phoneNumber.replace(/\D/g, "");
                                                    const dialCode = phoneNumber.match(/^\+(\d{1,3})/)?.[1] || "";
                                                    const nationalNumber = digitsOnly.slice(dialCode.length);

                                                    if (nationalNumber.length >= 9 && /[0-9]/.test(e.key)) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                inputProps={{
                                                    name: "phone_number",
                                                    required: true,
                                                }}
                                                inputClass="phone-input"
                                                containerClass="phone-container"
                                            />
                                        </div>
                                        
                                        {/* Phone availability status - only show if phone has changed and after checking */}
                                        {phoneNumber !== originalPhone && (
                                            <>
                                                {phoneChecking && (
                                                    <div className="phone-status checking">
                                                        Checking phone availability...
                                                    </div>
                                                )}
                                                
                                                {!phoneChecking && phoneCheckMessage && (
                                                    <div className={`phone-status ${phoneAvailable === true ? 'available' : phoneAvailable === false ? 'taken' : ''}`}>
                                                        {phoneCheckMessage}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        
                                        <div className="error-container">
                                            {phoneError && <p className="error-text">{phoneError}</p>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {step === 2 && (
                            <div>
                                <h3>Address</h3>
                                <hr />
                                <div className="form-grid">
                                    {/* Row 1: Address line 1 and Address line 2 side by side */}
                                    <div className="form-group">
                                        <label>Address line 1 <span className="required">*</span></label>
                                        <input 
                                            type="text" 
                                            value={addressLine1}
                                            onChange={(e) => {
                                                setAddressLine1(e.target.value);
                                                if (line1Error) setLine1Error(""); // clear error on typing
                                            }}
                                            placeholder="Enter address line 1" 
                                        />
                                        <div className="error-container">
                                            {line1Error && <p className="error-text">{line1Error}</p>}
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Address line 2</label>
                                        <input 
                                            type="text" 
                                            value={addressLine2}
                                            onChange={(e) => setAddressLine2(e.target.value)} 
                                            placeholder="Enter address line 2" 
                                        />
                                        <div className="error-container"/>
                                    </div>

                                    {/* Row 2: Address line 3 - full width */}
                                    <div className="form-group full-width">
                                        <label>Address line 3</label>
                                        <input 
                                            type="text" 
                                            value={addressLine3}
                                            onChange={(e) => setAddressLine3(e.target.value)}
                                            placeholder="Enter address line 3" 
                                        />
                                        <div className="error-container"/>
                                    </div>

                                    {/* Row 3: City and Region side by side */}
                                    <div className="form-group">
                                        <label>City <span className="required">*</span></label>
                                        <input
                                            type="text"
                                            value={city}
                                            onChange={(e) => {
                                                setCity(e.target.value);
                                                if (cityError) setCityError(""); // clear error on typing
                                            }}
                                            placeholder="Enter city"
                                        />
                                        <div className="error-container">
                                            {cityError && <p className="error-text">{cityError}</p>}
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Region <span className="required">*</span></label>
                                        <Select
                                            options={country ? regionOptions[country]?.map(r => ({ value: r, label: r })) || [] : []}
                                            value={currentRegionOption}
                                            onChange={(option) => setRegion(option.value)}
                                            placeholder="Select or type region"
                                            isSearchable
                                            classNamePrefix="custom-select"
                                            styles={{
                                                control: (base, state) => ({
                                                    ...base,
                                                    border: '1.17px solid rgba(209, 209, 209, 1)',
                                                    borderRadius: '17.6px',
                                                    background: 'rgba(253, 253, 253, 1)',
                                                    minHeight: '38px',
                                                    boxShadow: state.isFocused ? 'none' : 'none',
                                                    '&:hover': 'none',
                                                    fontSize: '14px',
                                                    color:'rgba(105, 105, 105, 1)',
                                                }),
                                                valueContainer: (base) => ({
                                                    ...base,
                                                    padding: '0 8px',
                                                }),
                                                singleValue: (base) => ({
                                                    ...base,
                                                    color:'black',
                                                }),
                                                placeholder: (base) => ({
                                                    ...base,
                                                    color:'rgba(105, 105, 105, 1)',
                                                }),
                                                menu: (base) => ({
                                                    ...base,
                                                    backgroundColor: '#fff',
                                                    borderRadius: '6px',
                                                    marginTop: '2px',
                                                    zIndex: 9999,
                                                }),
                                                option: (base, { isSelected, isFocused }) => ({
                                                    ...base,
                                                    backgroundColor: isSelected
                                                        ? '#e6f0ff'
                                                        : isFocused
                                                        ? '#e6f0ff'
                                                        : '#fff',
                                                    color: isSelected ? '#000' : '#000',
                                                    cursor: 'pointer',
                                                }),
                                            }}
                                        />
                                        <div className="error-container"/>
                                    </div>

                                    {/* Row 4: Post Code and Country side by side */}
                                    <div className="form-group">
                                        <label>Post Code <span className="required">*</span></label>
                                        <input
                                            type="text"
                                            value={postalCode}
                                            onChange={(e) => {
                                                setPostalCode(e.target.value);
                                                if (postalCodeError) setPostalCodeError(""); // clear error on typing
                                            }}
                                            placeholder="Enter postal code"
                                        />
                                        <div className="error-container">
                                            {postalCodeError && <p className="error-text">{postalCodeError}</p>}
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Country <span className="required">*</span></label>
                                        <Select
                                            options={Object.keys(regionOptions).map(c => ({ value: c, label: c }))}
                                            value={currentCountryOption}
                                            onChange={(option) => {
                                                setCountry(option.value);
                                                setRegion("");  // reset region
                                                // Auto-select first region if available
                                                const regions = regionOptions[option.value];
                                                if (regions && regions.length > 0) {
                                                    setRegion(regions[0]);
                                                }
                                            }}
                                            placeholder="Select or type country"
                                            isSearchable
                                            classNamePrefix="custom-select"
                                            styles={{
                                                control: (base, state) => ({
                                                    ...base,
                                                    border: '1.17px solid rgba(209, 209, 209, 1)',
                                                    borderRadius: '17.6px',
                                                    background: 'rgba(253, 253, 253, 1)',
                                                    minHeight: '38px',
                                                    boxShadow: state.isFocused ? 'none' : 'none',
                                                    '&:hover': 'none',
                                                    fontSize: '14px',
                                                    color:'rgba(105, 105, 105, 1)',
                                                }),
                                                valueContainer: (base) => ({
                                                    ...base,
                                                    padding: '0 8px',
                                                }),
                                                singleValue: (base) => ({
                                                    ...base,
                                                    color:'black',
                                                }),
                                                placeholder: (base) => ({
                                                    ...base,
                                                    color:'rgba(105, 105, 105, 1)',
                                                }),
                                                menu: (base) => ({
                                                    ...base,
                                                    backgroundColor: '#fff',
                                                    borderRadius: '6px',
                                                    marginTop: '2px',
                                                    zIndex: 9999,
                                                }),
                                                option: (base, { isSelected, isFocused }) => ({
                                                    ...base,
                                                    backgroundColor: isSelected
                                                        ? '#e6f0ff'
                                                        : isFocused
                                                        ? '#e6f0ff'
                                                        : '#fff',
                                                    color: isSelected ? '#000' : '#000',
                                                    cursor: 'pointer',
                                                }),
                                            }}
                                        />
                                        <div className="error-container"/>
                                    </div>
                                </div>
                            </div>
                        )}

                        {step === 3 && (
                            <div>
                                <h3>Status</h3>
                                <hr/>
                                <div className="form-grid center-step">        
                                    <div className="form-group">
                                        <label>
                                            Status
                                        </label>
                                        <Select
                                            options={statusOptions}
                                            value={status}
                                            onChange={setStatus}
                                            placeholder="Select Status"
                                            classNamePrefix="custom-select"
                                            styles={{
                                                control: (base, state) => ({
                                                    ...base,
                                                    border: '1.17px solid rgba(209, 209, 209, 1)',
                                                    borderRadius: '17.6px',
                                                    background: 'rgba(253, 253, 253, 1)',
                                                    minHeight: '38px',
                                                    boxShadow: state.isFocused ? '0 0 0px rgba(21, 21, 22, 0.4)' : 'none',
                                                    '&:hover': 'none',
                                                    fontSize: '15px',
                                                    color:'black',
                                                }),
                                                valueContainer: (base) => ({
                                                    ...base,
                                                    padding: '0 8px',
                                                }),
                                                singleValue: (base) => ({
                                                    ...base,
                                                    color:'black',
                                                }),
                                                placeholder: (base) => ({
                                                    ...base,
                                                    color:'rgba(105, 105, 105, 1)',
                                                }),
                                                menu: (base) => ({
                                                    ...base,
                                                    backgroundColor: '#fff',
                                                    borderRadius: '6px',
                                                    marginTop: '2px',
                                                    zIndex: 9999,
                                                }),
                                                option: (base, { isSelected, isFocused }) => ({
                                                    ...base,
                                                    backgroundColor: isSelected
                                                        ? '#e6f0ff'
                                                        : isFocused
                                                        ? '#e6f0ff'
                                                        : '#fff',
                                                    color: isSelected ? '#000' : '#000',
                                                    cursor: 'pointer',
                                                }),
                                            }}
                                        />
                                    </div>  

                                    <div className="form-group">
                                        <label>Notes</label>
                                        <textarea
                                            className="note-textarea"
                                            rows="8"
                                            value={note}
                                            onChange={(e) => setNote(e.target.value)}
                                            placeholder="Enter note here"
                                        />
                                    </div>              
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Navigation Buttons */}
                    <div className="btn-row">
                        {step === 3 && (
                            <button className="btn prev" onClick={handleCancel} disabled={loading}>
                                Cancel
                            </button>
                        )}

                        {step > 1 && (
                            <button className="btn prev" onClick={prevStep} disabled={loading}>
                                Previous
                            </button>
                        )}
                        {step < 3 && (
                            <button 
                                className="btn next" 
                                onClick={nextStep} 
                                disabled={loading || emailChecking || phoneChecking}
                                style={{
                                    opacity: (!loading && !emailChecking && !phoneChecking) ? 1 : 0.6,
                                    cursor: (!loading && !emailChecking && !phoneChecking) ? "pointer" : "not-allowed",
                                }}
                            >
                                {(emailChecking || phoneChecking) ? "Checking..." : "Next"}
                            </button>
                        )}
                        {step === 3 && (
                            <button className="btn next" onClick={handleUpdate} disabled={loading}>
                                {loading ? "Updating..." : "Update"}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>      
    );
}

export default EditCustomerProfile;
